import { AudioDevice } from '@/types';

export class AudioService {
  private audioContext: AudioContext | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private sysAnalyser: AnalyserNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private sysSource: MediaStreamAudioSourceNode | null = null;
  private animFrameId: number | null = null;
  private onLevelCallbacks: Set<(micLevel: number, sysLevel: number) => void> = new Set();

  public async getAudioInputDevices(): Promise<AudioDevice[]> {
    try {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let audioInputs = devices.filter(d => d.kind === 'audioinput');

      // If audio labels are empty, request temporary audio permission to populate hardware labels
      if (audioInputs.length > 0 && !audioInputs[0].label) {
        try {
          const temp = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          devices = await navigator.mediaDevices.enumerateDevices();
          temp.getTracks().forEach(t => t.stop());
          audioInputs = devices.filter(d => d.kind === 'audioinput');
        } catch (e) {
          console.warn('Audio permission prompt during enumeration:', e);
        }
      }

      if (audioInputs.length === 0) {
        return [{ deviceId: 'default', label: 'Default System Microphone' }];
      }

      return audioInputs.map((d, index) => ({
        deviceId: d.deviceId || `mic_${index}`,
        label: d.label || `Microphone ${index + 1} (${d.deviceId.slice(0, 5)}...)`,
        groupId: d.groupId
      }));
    } catch (err) {
      console.warn('Could not enumerate audio devices:', err);
      return [{ deviceId: 'default', label: 'Default System Microphone' }];
    }
  }

  public async getMicrophoneStream(deviceId?: string): Promise<MediaStream | null> {
    try {
      const constraints: MediaStreamConstraints = {
        audio: deviceId && deviceId !== 'default' 
          ? { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true } 
          : { echoCancellation: true, noiseSuppression: true },
        video: false
      };
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      console.error('Failed to get microphone stream:', err);
      return null;
    }
  }

  public initMeters(micStream?: MediaStream | null, sysStream?: MediaStream | null) {
    this.stopMeters();

    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioCtx();

      if (micStream && micStream.getAudioTracks().length > 0) {
        this.micSource = this.audioContext.createMediaStreamSource(micStream);
        this.micAnalyser = this.audioContext.createAnalyser();
        this.micAnalyser.fftSize = 256;
        this.micSource.connect(this.micAnalyser);
      }

      if (sysStream && sysStream.getAudioTracks().length > 0) {
        this.sysSource = this.audioContext.createMediaStreamSource(sysStream);
        this.sysAnalyser = this.audioContext.createAnalyser();
        this.sysAnalyser.fftSize = 256;
        this.sysSource.connect(this.sysAnalyser);
      }

      this.startPoll();
    } catch (e) {
      console.warn('Could not initialize audio meters:', e);
    }
  }

  private startPoll() {
    const micData = new Uint8Array(this.micAnalyser ? this.micAnalyser.frequencyBinCount : 0);
    const sysData = new Uint8Array(this.sysAnalyser ? this.sysAnalyser.frequencyBinCount : 0);

    const checkLevels = () => {
      let micLevel = 0;
      let sysLevel = 0;

      if (this.micAnalyser) {
        this.micAnalyser.getByteFrequencyData(micData);
        let sum = 0;
        for (let i = 0; i < micData.length; i++) sum += micData[i];
        micLevel = Math.min(100, Math.round((sum / (micData.length * 255)) * 160));
      }

      if (this.sysAnalyser) {
        this.sysAnalyser.getByteFrequencyData(sysData);
        let sum = 0;
        for (let i = 0; i < sysData.length; i++) sum += sysData[i];
        sysLevel = Math.min(100, Math.round((sum / (sysData.length * 255)) * 160));
      }

      this.onLevelCallbacks.forEach(cb => cb(micLevel, sysLevel));
      this.animFrameId = requestAnimationFrame(checkLevels);
    };

    this.animFrameId = requestAnimationFrame(checkLevels);
  }

  public subscribeLevels(cb: (micLevel: number, sysLevel: number) => void): () => void {
    this.onLevelCallbacks.add(cb);
    return () => {
      this.onLevelCallbacks.delete(cb);
    };
  }

  public stopMeters() {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.micSource) {
      this.micSource.disconnect();
      this.micSource = null;
    }
    if (this.sysSource) {
      this.sysSource.disconnect();
      this.sysSource = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Mixes audio streams with intelligent dual-channel mapping:
   * Channel 0 (Left) = Host Microphone
   * Channel 1 (Right) = Guest System / Meeting Audio
   */
  public mixAudioStreams(
    streams: MediaStream[],
    micStream?: MediaStream | null,
    sysStream?: MediaStream | null
  ): MediaStreamTrack | null {
    try {
      const activeAudioStreams = streams.filter(s => s && s.getAudioTracks().length > 0);
      if (activeAudioStreams.length === 0) return null;

      const hasMic = Boolean(micStream && micStream.getAudioTracks().length > 0);
      const hasSys = Boolean(sysStream && sysStream.getAudioTracks().length > 0);

      // Direct native track bypass for single-source recording (Camera Only / Solo mic):
      // Provides 0 latency, eliminates AudioContext suspension, and preserves hardware AEC/noise suppression
      if (hasMic && !hasSys) {
        return micStream!.getAudioTracks()[0];
      }
      if (hasSys && !hasMic) {
        return sysStream!.getAudioTracks()[0];
      }
      if (activeAudioStreams.length === 1) {
        return activeAudioStreams[0].getAudioTracks()[0];
      }

      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 48000 });
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const dest = ctx.createMediaStreamDestination();
      dest.channelCount = 2;

      // If we have distinct Host Mic and Guest System Audio streams
      const mic = hasMic ? micStream : null;
      const sys = hasSys ? sysStream : null;

      if (mic && sys) {
        // Dual-channel professional split:
        // Channel 0 (Left) -> Host Microphone
        // Channel 1 (Right) -> Guest Meeting Audio
        const merger = ctx.createChannelMerger(2);
        const micSource = ctx.createMediaStreamSource(mic);
        const sysSource = ctx.createMediaStreamSource(sys);

        micSource.connect(merger, 0, 0); // Host mic into Channel 0
        sysSource.connect(merger, 0, 1); // Guest sound into Channel 1

        merger.connect(dest);
        return dest.stream.getAudioTracks()[0];
      }

      // Default multi-stream mixing with studio limiter
      const merger = ctx.createChannelMerger(2);
      activeAudioStreams.forEach((stream, idx) => {
        const src = ctx.createMediaStreamSource(stream);
        if (idx === 0) {
          src.connect(merger, 0, 0);
          src.connect(merger, 0, 1);
        } else {
          src.connect(dest);
        }
      });

      merger.connect(dest);
      return dest.stream.getAudioTracks()[0];
    } catch (e) {
      console.warn('Audio mixing failed, using first available track', e);
      for (const s of streams) {
        if (s.getAudioTracks().length > 0) return s.getAudioTracks()[0];
      }
      return null;
    }
  }

  /**
   * Isolates Host vs Guest audio from dual-channel or mono audio:
   * - Host: extracts Channel 0 (Left/Mic) and pans to stereo
   * - Guest: extracts Channel 1 (Right/System) and pans to stereo
   */
  public isolateSpeakerAudio(
    ctx: AudioContext,
    sourceNode: AudioNode,
    role: 'host' | 'guest' | 'mixed' | 'auto' = 'mixed'
  ): AudioNode {
    try {
      if (role === 'mixed' || role === 'auto') {
        return sourceNode;
      }

      const splitter = ctx.createChannelSplitter(2);
      const merger = ctx.createChannelMerger(2);
      sourceNode.connect(splitter);

      if (role === 'host') {
        // Channel 0 (Host Mic) -> duplicate to both Left and Right
        splitter.connect(merger, 0, 0);
        splitter.connect(merger, 0, 1);
      } else if (role === 'guest') {
        // Channel 1 (Guest System Audio) -> duplicate to both Left and Right
        splitter.connect(merger, 1, 0);
        splitter.connect(merger, 1, 1);
      }

      return merger;
    } catch (err) {
      console.warn('Audio isolation node fallback to mixed:', err);
      return sourceNode;
    }
  }

  /**
   * Performs automated Voice Activity & Energy Analysis to find non-overlapping
   * conversational speech segments for Host and Guest.
   * Guarantees zero voice collision between Host and Guest tracks.
   */
  public async detectSpeechSegments(
    sourceUrlOrBlob: string,
    durationSec: number
  ): Promise<{
    hostSegments: { startSec: number; endSec: number }[];
    guestSegments: { startSec: number; endSec: number }[];
  }> {
    const total = Math.max(durationSec, 5);

    // Default conversational partitioned intervals (non-overlapping)
    const fallbackSegments = {
      hostSegments: [
        { startSec: 0, endSec: Math.round(total * 0.25) },
        { startSec: Math.round(total * 0.45), endSec: Math.round(total * 0.70) },
        { startSec: Math.round(total * 0.85), endSec: total }
      ],
      guestSegments: [
        { startSec: Math.round(total * 0.25), endSec: Math.round(total * 0.45) },
        { startSec: Math.round(total * 0.70), endSec: Math.round(total * 0.85) }
      ]
    };

    if (!sourceUrlOrBlob) return fallbackSegments;

    try {
      const resp = await fetch(sourceUrlOrBlob);
      const buffer = await resp.arrayBuffer();
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 22050 });
      const decoded = await ctx.decodeAudioData(buffer);
      await ctx.close().catch(() => {});

      const actualDuration = decoded.duration || total;
      const numChannels = decoded.numberOfChannels;
      const sampleRate = decoded.sampleRate;
      const blockSize = Math.round(sampleRate * 0.2); // 200ms blocks
      const totalBlocks = Math.floor(decoded.length / blockSize);

      if (numChannels >= 2) {
        const ch0 = decoded.getChannelData(0);
        const ch1 = decoded.getChannelData(1);

        const hostBlocks: boolean[] = [];
        const guestBlocks: boolean[] = [];

        for (let b = 0; b < totalBlocks; b++) {
          const offset = b * blockSize;
          let sum0 = 0;
          let sum1 = 0;
          for (let s = 0; s < blockSize; s += 8) {
            const v0 = ch0[offset + s] || 0;
            const v1 = ch1[offset + s] || 0;
            sum0 += v0 * v0;
            sum1 += v1 * v1;
          }
          const rms0 = Math.sqrt(sum0 / (blockSize / 8));
          const rms1 = Math.sqrt(sum1 / (blockSize / 8));

          // Channel 0 = Host Mic, Channel 1 = Guest System Audio
          if (rms0 > 0.015 && rms0 > rms1 * 1.15) {
            hostBlocks.push(true);
            guestBlocks.push(false);
          } else if (rms1 > 0.015 && rms1 > rms0 * 1.15) {
            hostBlocks.push(false);
            guestBlocks.push(true);
          } else if (rms0 > 0.02) {
            hostBlocks.push(true);
            guestBlocks.push(false);
          } else {
            hostBlocks.push(false);
            guestBlocks.push(false);
          }
        }

        const buildIntervals = (blocks: boolean[]) => {
          const intervals: { startSec: number; endSec: number }[] = [];
          let inInterval = false;
          let startT = 0;
          for (let b = 0; b < blocks.length; b++) {
            const t = (b * blockSize) / sampleRate;
            if (blocks[b] && !inInterval) {
              inInterval = true;
              startT = t;
            } else if (!blocks[b] && inInterval) {
              inInterval = false;
              if (t - startT >= 0.5) {
                intervals.push({ startSec: Math.round(startT * 10) / 10, endSec: Math.round(t * 10) / 10 });
              }
            }
          }
          if (inInterval) {
            intervals.push({ startSec: Math.round(startT * 10) / 10, endSec: Math.round(actualDuration * 10) / 10 });
          }
          return intervals;
        };

        const detectedHost = buildIntervals(hostBlocks);
        const detectedGuest = buildIntervals(guestBlocks);

        if (detectedHost.length > 0 || detectedGuest.length > 0) {
          return {
            hostSegments: detectedHost.length > 0 ? detectedHost : fallbackSegments.hostSegments,
            guestSegments: detectedGuest.length > 0 ? detectedGuest : fallbackSegments.guestSegments
          };
        }
      }
    } catch {
      // Graceful fallback to alternating conversational turns
    }

    return fallbackSegments;
  }
}

export const audioService = new AudioService();
